import {DPNode, DPSearchBlock} from 'types/DPTypes'
import {
	Highlight as HighlightDef,
	HighlightProps,
	InfiniteHits as InfiniteHitsDef,
	InfiniteHitsProps,
	InstantSearch,
	InstantSearchProps,
	RefinementList as RefinementListDef,
	RefinementListProps,
	Snippet as SnippetDef,
	SnippetProps,
	useHits,
	useInfiniteHits,
	useInstantSearch
} from 'react-instantsearch-hooks-web'
import React, {Suspense, useMemo} from 'react'
import {
	SearchresultsEmpty,
	SearchresultsLoader
} from 'layout/search/searchresults'
import {en, nl} from 'locale'

import {Autocomplete} from 'blocks/search/Autocomplete'
import {Breadcrumbs} from 'layout/partials/breadcrumbs'
import {Image} from 'layout/partials/image'
import {SearchresultsMore} from 'layout/search/searchresults'
import {Theme} from 'layout/theme'
import algoliasearch from 'algoliasearch/lite'
import css from './search.module.scss'
import {drupalLang} from 'util/locale'
import {fromModule} from 'util/styler/Styler'
import {history} from 'instantsearch.js/es/lib/routers/index.js'
import {useGetParents} from 'blocks/hero/hero'
import {useMenu} from 'react-instantsearch-hooks-web'
import {useTranslation} from 'util/i18'

const styles = fromModule(css)

const Highlight = HighlightDef as unknown as React.FC<HighlightProps<any>>
const Snippet = SnippetDef as unknown as React.FC<SnippetProps<any>>
const InfiniteHits = InfiniteHitsDef as unknown as React.FC<
	InfiniteHitsProps<any>
>
const RefinementList =
	RefinementListDef as unknown as React.FC<RefinementListProps>

export type SearchType = {
	button: string
	more: string
	placeholder: string
	categories: Array<string>
}

const algoliaClient = algoliasearch(
	process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
	process.env.NEXT_PUBLIC_ALGOLIA_API_KEY
)

const searchClient: typeof algoliaClient = {
	...algoliaClient,
	search(requests) {
		if (requests.every(({params}) => !params.query)) {
			return Promise.resolve({
				results: requests.map(() => ({
					__isArtificial: true,
					hits: [],
					nbHits: 0,
					nbPages: 0,
					page: 0,
					processingTimeMS: 0,
					hitsPerPage: 0,
					exhaustiveNbHits: false,
					query: '',
					params: ''
				}))
			})
		}
		return algoliaClient.search(requests)
	}
}

export const Search: React.FC<DPSearchBlock> = ({categories, popular_tags}) => {
	const routing = useMemo(() => {
		const index = process.env.NEXT_PUBLIC_ALGOLIA_INDEX + '_pages'
		return {
			router: history({
				writeDelay: 800
			}),
			stateMapping: {
				stateToRoute(uiState) {
					return {
						q: uiState[index].query
					}
				},
				routeToState(routeState) {
					return {
						[index]: {
							query: routeState.q
						}
					}
				}
			}
		}
	}, [])

	const InstantSearchView =
		InstantSearch as unknown as React.FC<InstantSearchProps>
	return (
		<InstantSearchView
			searchClient={searchClient}
			indexName={process.env.NEXT_PUBLIC_ALGOLIA_INDEX + '_pages'}
			routing={routing}
		>
			<div className={styles.search()}>
				<div className={styles.searchbar()}>
					<Theme.Container>
						<div className={styles.searchbar.flex()}>
							<div className={styles.searchbar.flex.input()}>
								<Autocomplete
									searchClient={searchClient}
									placeholder="Search"
									detachedMediaQuery="none"
									autoFocus
									qsIndex={process.env.NEXT_PUBLIC_ALGOLIA_INDEX + '_pages_qs'}
								/>
							</div>
						</div>
						<SearchFilter />
					</Theme.Container>
				</div>
				<SearchResults />
			</div>
		</InstantSearchView>
	)
}

const categories = ['press', 'article', 'job', 'event']
const transformItems: React.ComponentProps<
	typeof RefinementList
>['transformItems'] = (items) => {
	const t = drupalLang === 'nl' ? nl : en
	return categories.map((category) => ({
		...items.find((item) => item.value === category),
		label: t.search.categories[category]
	}))
}

const SearchFilter: React.FC = () => {
	const {items, refine} = useMenu({
		attribute: 'type',
		transformItems
	})
	const t = useTranslation()
	const activeItem = items.find((item) => item?.isRefined)

	return (
		<div className={styles.filter()}>
			{t.search.categories_label}
			<span className={styles.filter.topics()}>
				<div
					className={styles.filter.topics.item.is({
						active: !activeItem,
						useable: true
					})()}
					onClick={() => {
						if (activeItem) refine(null)
					}}
				>
					{t.search.all}
				</div>
				{['press', 'article', 'job', 'event'].map((value) => {
					const item = items.find((item) => item.value === value)
					const active = item?.isRefined
					const useable = item?.count
					return (
						<div
							key={value}
							className={styles.filter.topics.item.is({active, useable})()}
							onClick={() => {
								if (useable) refine(value)
							}}
						>
							{value}
						</div>
					)
				})}
			</span>
		</div>
	)
}

export const SearchResults: React.FC = () => {
	const {hits} = useHits()
	const instantSearch = useInstantSearch()

	if (hits?.length === 0) {
		if (
			!instantSearch.results.__isArtificial &&
			instantSearch.results.nbHits === 0
		) {
			return (
				<Suspense>
					<SearchresultsEmpty />
				</Suspense>
			)
		} else if (instantSearch.status === 'stalled') {
			return <SearchresultsLoader />
		} else {
			return null
		}
	}

	return (
		<div className={styles.results()}>
			<div className={styles.results.items()}>
				<Hits />
			</div>
		</div>
	)
}

const Hits: React.FC = () => {
	const {hits, isLastPage, showMore, sendEvent} = useInfiniteHits({
		showPrevious: false
	})

	return (
		<div>
			{hits.map((hit) => (
				<SearchItem key={hit.objectID} hit={hit} sendEvent={sendEvent} />
			))}
			{!isLastPage && showMore && (
				<SearchresultsMore onClick={showMore}>Load more</SearchresultsMore>
			)}
		</div>
	)
}

export const SearchItem: React.ComponentProps<
	typeof InfiniteHits
>['hitComponent'] = ({hit, sendEvent}) => {
	const node: DPNode = useMemo(
		() => JSON.parse(hit.node as any),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[hit.objectID]
	)
	const parents = useGetParents(node)
	const descriptionSnippet = hit._snippetResult?.description
	const descriptionMatch =
		descriptionSnippet &&
		'matchLevel' in descriptionSnippet &&
		descriptionSnippet.matchLevel !== 'none'

	const isDossier = node && node._type === 'dossier'
	const customParents =
		node && (node as any)._type === 'euproject' && (node as any).custom_parents
			? (node as any).custom_parents
			: parents

	return (
		<div className={styles.results.result()}>
			<Theme.Container>
				<div className={styles.result.container.mod({dossier: isDossier})()}>
					{isDossier && node.image && node.image.src && (
						<div className={styles.result.dossier.image()}>
							<Image {...node.image} fill sizes="200px" />
						</div>
					)}
					<div className={styles.result.content.mod({dossier: isDossier})()}>
						<div className={styles.results.result.title()}>
							<Theme.H4>
								{isDossier && (
									<span className={styles.result.dossier.tag()}>
										{'Dossier'}
									</span>
								)}
								<a
									href={node.url}
									target="_self"
									className={styles.results.result.title.link()}
								>
									<Highlight attribute="title" hit={hit} />
								</a>
							</Theme.H4>
						</div>
						<div>
							<p>
								{descriptionMatch ? (
									<Snippet attribute="description" hit={hit} />
								) : (
									<Snippet attribute="content" hit={hit} />
								)}
							</p>
						</div>
						<div className={styles.results.result.breadcrumbs()}>
							<Breadcrumbs
								parents={customParents}
								pageTitle={node.title}
								pageurl={node.url}
								mod="small"
							/>
						</div>
					</div>
				</div>
			</Theme.Container>
		</div>
	)
}
